import type { Card } from "../types/card";
import { isWild } from "../types/card";
import type { DroughtState, GameState, StackIndex } from "../types/game";
import { STACK_INDICES } from "../types/game";
import { normalizeHouseRules, type HouseRules } from "./houseRules";
import { starfallShuffle } from "./starfall";

/**
 * Drought: the counter-streak. A player who takes a pile on three turns in a
 * row is wrapped in mist, and the next pile they take only half comes to them -
 * the mist bursts it from the middle and blows the lower half out onto the
 * other piles. A catch-up rule for the player having the run of bad luck,
 * the mirror of Jourdan's Streaks (five clean turns light a fire).
 *
 * The count is the player's (`PlayerState.drought`) and moves in two places:
 * up by one when a pile is taken (`takeStack`), back to zero at the end of a
 * turn that took none (`endTurn`). Reaching `DROUGHT_MISSES_TO_MIST` arms the
 * mist, which then stays until it is spent - a clean turn does not blow it
 * away. Spending it starts the count again from zero: the miss that used it
 * is not the first of the next three.
 *
 * This file is the arithmetic. The burst itself is `mistStack` (moves.ts).
 *
 * Mirrors services/game-server/internal/engine/drought.go. The two step the
 * same generator, but seeds differ between languages (as Starfall's do).
 */

/** Piles taken on consecutive turns that wrap a deck in mist. */
export const DROUGHT_MISSES_TO_MIST = 3;
/** Fewest seats for the rule to mean anything: a catch-up needs somebody to catch. */
export const DROUGHT_MIN_PLAYERS = 2;
/**
 * The fewest cards a misted take still brings home: the card that was guessed
 * against and the card that missed it. They are what keeps a missed guess from
 * ever being a win (see `refillTakenStack`), so the mist never scatters them.
 */
export const DROUGHT_MIN_KEPT = 2;

/** Whether Drought is in force at this table: the flag, plus an opponent. */
export function droughtApplies(rules: HouseRules, playerCount: number): boolean {
  return rules.droughtRule && playerCount >= DROUGHT_MIN_PLAYERS;
}

/**
 * The table's Drought generator when the rule is in force, else null. Absent
 * state reads as OFF, as Mercy's and Starfall's do, so hand-built states (engine
 * tests, the tutorial, staged boards) never count or mist without asking.
 */
export function activeDrought(state: GameState): DroughtState | null {
  const d = state.drought;
  if (!d || !droughtApplies(normalizeHouseRules(state.houseRules), state.players.length)) return null;
  return d;
}

/** Whether a deck with this count is wrapped in mist. */
export function isMisted(drought: number | undefined): boolean {
  return (drought ?? 0) >= DROUGHT_MISSES_TO_MIST;
}

/**
 * How a misted take splits the pile, given top-first: the cards that still come
 * to the player, and the cards the mist scatters, both in pile order.
 *
 * The player keeps the top half, rounded up, and never fewer than
 * `DROUGHT_MIN_KEPT`. The mist never scatters a wild - a wild in the lower half
 * comes home with the top half, in the place it held in the pile, so a misted
 * take can only ever be better than a plain one. Nothing to scatter (a pile of
 * two, or a lower half of wilds) leaves `scattered` empty, and the mist is not
 * spent.
 */
export function droughtSplit(cards: readonly Card[]): { kept: Card[]; scattered: Card[] } {
  const keep = Math.max(DROUGHT_MIN_KEPT, Math.floor((cards.length + 1) / 2));
  const kept: Card[] = [];
  const scattered: Card[] = [];
  cards.forEach((card, i) => {
    if (i < keep || isWild(card)) kept.push(card);
    else scattered.push(card);
  });
  return { kept, scattered };
}

/**
 * The piles `count` scattered cards land on, in landing order: laps of the
 * OTHER four piles - never the one being taken, which its player refills - in a
 * fresh random order each lap, so every pile takes one before any takes a
 * second (as a Mercy burst spreads).
 */
export function droughtPiles(d: DroughtState, from: StackIndex, count: number): StackIndex[] {
  const others = STACK_INDICES.filter((i) => i !== from);
  const out: StackIndex[] = [];
  while (out.length < count) out.push(...starfallShuffle(d, others));
  return out.slice(0, count);
}

/** A fresh generator for a round being dealt. */
export function newDroughtState(seed: number): DroughtState {
  return { rng: seed >>> 0 };
}
